import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAccountsForSelect, useSalesUsers, useCreateSubscription } from '../../hooks';
import type { SubscriptionStatus } from '../../types';
import { SUBSCRIPTION_STATUSES } from '../../types';

interface CreateSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (subscriptionId: string) => void;
  initialCustomerId?: string;
  initialCustomerName?: string;
  initialCustomerAccountNumber?: string;
  initialSalesManagerId?: string;
}

export function CreateSubscriptionModal({
  open,
  onOpenChange,
  onCreated,
  initialCustomerId,
  initialCustomerName,
  initialCustomerAccountNumber,
  initialSalesManagerId,
}: CreateSubscriptionModalProps) {
  const { t } = useTranslation('subscriptions');

  const [accountId, setAccountId] = useState('');
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const debouncedAccountSearch = useDebounce(accountSearch, 300);
  const [name, setName] = useState('');
  const [salesManagerId, setSalesManagerId] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus>('draft');
  const [currency, setCurrency] = useState('CAD');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [gdriveLink, setGdriveLink] = useState('');

  const accountsQuery = useAccountsForSelect(debouncedAccountSearch || undefined);
  const salesUsersQuery = useSalesUsers();
  const createMutation = useCreateSubscription();

  const accounts = accountsQuery.data || [];
  const salesUsers = salesUsersQuery.data || [];

  const normalizeForMatch = (value: string | undefined): string => {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAccountId('');
      setName('');
      setSalesManagerId('');
      setStatus('draft');
      setCurrency('CAD');
      setPaymentTerms('');
      setStartDate('');
      setEndDate('');
      setGdriveLink('');
    }
  }, [open]);

  // Prefill account and manager when opened from account dashboard context
  useEffect(() => {
    if (!open || accountId || accounts.length === 0) return;

    const normalizedAccountNumber = (initialCustomerAccountNumber || '').replace(/^0+/, '');
    const normalizedInitialName = normalizeForMatch(initialCustomerName);
    const customerById = initialCustomerId
      ? accounts.find((account) => account.id === initialCustomerId)
      : undefined;
    const customerByAccountNumber = normalizedAccountNumber
      ? accounts.find((account) => String(account.accountNumber || '').replace(/^0+/, '') === normalizedAccountNumber)
      : undefined;
    const customerByExactName = normalizedInitialName
      ? accounts.find((account) => normalizeForMatch(account.name) === normalizedInitialName)
      : undefined;
    const customerByFuzzyName = normalizedInitialName
      ? accounts.find((account) => {
          const normalizedAccountName = normalizeForMatch(account.name);
          return normalizedAccountName.includes(normalizedInitialName)
            || normalizedInitialName.includes(normalizedAccountName);
        })
      : undefined;

    const matchedCustomer =
      customerById
      || customerByAccountNumber
      || customerByExactName
      || customerByFuzzyName;

    if (matchedCustomer) {
      setAccountId(matchedCustomer.id);
    }
  }, [
    open,
    accountId,
    accounts,
    initialCustomerId,
    initialCustomerName,
    initialCustomerAccountNumber,
  ]);

  useEffect(() => {
    if (!open || !initialSalesManagerId || salesManagerId) return;
    if (salesUsers.some((user) => user._id === initialSalesManagerId)) {
      setSalesManagerId(initialSalesManagerId);
    }
  }, [open, initialSalesManagerId, salesManagerId, salesUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountId || !name) return;

    try {
      const result = await createMutation.mutateAsync({
        accountId,
        name,
        salesManagerId: salesManagerId || undefined,
        status,
        currency,
        paymentTerms: paymentTerms || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        gdriveFolderId: gdriveLink || undefined,
      });
      onCreated?.(result.subscription.id);
      onOpenChange(false);
    } catch {
      // Error handled by mutation toast
    }
  };

  const isValid = accountId && name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('form.createTitle')}</DialogTitle>
            <DialogDescription>
              {t('form.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Account */}
            <div className="grid gap-2">
              <Label>{t('fields.account')}</Label>
              <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={accountSearchOpen}
                    className="w-full justify-between font-normal"
                  >
                    {accountId
                      ? (() => { const a = accounts.find((a) => a.id === accountId); return a ? `${a.name} (${a.accountNumber})` : accountId; })()
                      : t('fields.selectAccount')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('fields.selectAccount')}
                      value={accountSearch}
                      onValueChange={setAccountSearch}
                    />
                    <CommandList>
                      <CommandEmpty>{accountsQuery.isLoading ? t('filters.loading') : 'No accounts found'}</CommandEmpty>
                      <CommandGroup>
                        {accounts.map((account) => (
                          <CommandItem
                            key={account.id}
                            value={account.id}
                            onSelect={() => {
                              setAccountId(account.id);
                              setAccountSearchOpen(false);
                              setAccountSearch('');
                            }}
                          >
                            <Check className={cn('mr-2 h-4 w-4', accountId === account.id ? 'opacity-100' : 'opacity-0')} />
                            {account.name} ({account.accountNumber})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">{t('fields.contractNameRequired')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('fields.contractNamePlaceholder')}
              />
            </div>

            {/* Status */}
            <div className="grid gap-2">
              <Label>{t('fields.status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as SubscriptionStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBSCRIPTION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sales Manager */}
            <div className="grid gap-2">
              <Label>{t('fields.salesManager')}</Label>
              <Select
                value={salesManagerId || undefined}
                onValueChange={setSalesManagerId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('fields.selectSalesManager')} />
                </SelectTrigger>
                <SelectContent>
                  {salesUsers.map((u) => (
                    <SelectItem key={u._id} value={u._id}>
                      {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u._id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Currency & Payment Terms */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('fields.currency')}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>{t('fields.paymentTerms')}</Label>
                <Select
                  value={paymentTerms || 'none'}
                  onValueChange={(v) => setPaymentTerms(v === 'none' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectPaymentTerms')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('paymentTerms.none')}</SelectItem>
                    <SelectItem value="on-order">{t('paymentTerms.on-order')}</SelectItem>
                    <SelectItem value="net-0">{t('paymentTerms.net-0')}</SelectItem>
                    <SelectItem value="net-30">{t('paymentTerms.net-30')}</SelectItem>
                    <SelectItem value="net-45">{t('paymentTerms.net-45')}</SelectItem>
                    <SelectItem value="net-60">{t('paymentTerms.net-60')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('fields.startDate')}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('fields.endDate')}</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* Google Drive Folder ID */}
            <div className="grid gap-2">
              <Label>{t('fields.gdriveLink')}</Label>
              <Input
                value={gdriveLink}
                onChange={(e) => setGdriveLink(e.target.value)}
                placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('form.cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending ? t('form.creating') : t('form.createTitle')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
